import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import api from "@/lib/api";
import { MapPin, Phone, Users, PlusCircle } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

export default function ClubsPage() {
  const [clubs, setClubs] = useState([]);
  const [district, setDistrict] = useState("all");
  useEffect(() => { api.get("/clubs").then((r) => setClubs(r.data)); }, []);

  const districts = useMemo(() => Array.from(new Set(clubs.map((c) => c.district))).sort(), [clubs]);
  const filtered = district === "all" ? clubs : clubs.filter((c) => c.district === district);

  return (
    <div data-testid="clubs-page">
      <PageHero eyebrow="Places to play" title="CLUBS" subtitle="Official JPA-affiliated pickleball clubs across Jharkhand."
        image="https://images.unsplash.com/photo-1580763850522-504d40a05c50?w=1800" />
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary">Filter by district</div>
            <Select value={district} onValueChange={setDistrict}>
              <SelectTrigger data-testid="clubs-district-filter" className="rounded-sm w-full sm:w-64"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All districts ({clubs.length})</SelectItem>
                {districts.map((d) => (
                  <SelectItem key={d} value={d}>{d} ({clubs.filter((c) => c.district === d).length})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-sm text-jpa-muted">Showing <b>{filtered.length}</b> {filtered.length === 1 ? "club" : "clubs"}</div>
          </div>
          <Link to="/register-club">
            <Button data-testid="register-club-cta" className="bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest font-bold rounded-sm">
              <PlusCircle className="w-4 h-4 mr-2" /> Register your club
            </Button>
          </Link>
        </div>

        {filtered.length === 0 ? (
          <div className="py-16 text-center text-jpa-muted">No clubs in this district yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((c) => (
              <div key={c.id} data-testid={`club-card-${c.id}`} className="border border-jpa-border overflow-hidden bg-white hover:-translate-y-1 hover:shadow-xl transition-transform">
                <div className="aspect-[16/10] overflow-hidden bg-jpa-surface">
                  {c.image_url && <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" />}
                </div>
                <div className="p-5">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary">{c.district}</div>
                  <h3 className="font-display text-2xl tracking-wider mt-1">{c.name}</h3>
                  <ul className="mt-3 space-y-1.5 text-sm text-jpa-muted">
                    <li className="flex items-start gap-2"><MapPin className="w-4 h-4 text-jpa-primary mt-0.5 flex-shrink-0" /> {c.address}</li>
                    <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-jpa-primary flex-shrink-0" /> {c.phone}</li>
                    <li className="flex items-center gap-2"><Users className="w-4 h-4 text-jpa-primary flex-shrink-0" /> {c.courts} courts • {c.contact_person}</li>
                  </ul>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
